import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, questionsAPI } from '../services/api';
import './Discovery.css';

const MIN_WORDS = 5;
const MAX_WORDS = 500;

const Discovery = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [componentVideos, setComponentVideos] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [foundationErrors, setFoundationErrors] = useState(new Set()); // Track unanswered Foundation questions
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    fetchData();
    loadComponentVideos();
  }, [projectId]);

  const loadComponentVideos = async () => {
    try {
      const response = await fetch('/componentVideos.json');
      const data = await response.json();
      setComponentVideos(data.videos);
    } catch (err) {
      console.warn('Could not load component videos:', err);
    }
  };

  // Auto-save every 30 seconds if there are changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (Object.keys(formData).length > 0) {
        handleSave(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [formData]);

  const fetchData = async () => {
    try {
      const [projectRes, questionsRes] = await Promise.all([
        projectsAPI.getById(projectId),
        questionsAPI.getAll(),
      ]);

      setProject(projectRes.data.data);
      setQuestions(questionsRes.data.data);

      // Load existing form data
      const existingData = projectRes.data.data.formData;
      if (existingData) {
        const dataObj =
          existingData instanceof Map
            ? Object.fromEntries(existingData)
            : existingData;
        setFormData(dataObj);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load project data');
      setLoading(false);
    }
  };

  const validateAnswer = (answer) => {
    if (!answer || !answer.trim()) {
      return { valid: true, wordCount: 0 }; // Empty is OK, just won't count as answered
    }

    const wordCount = answer.trim().split(/\s+/).filter(w => w.length > 0).length;

    if (wordCount < MIN_WORDS) {
      return {
        valid: false,
        wordCount,
        error: `Answer must contain at least ${MIN_WORDS} words (currently ${wordCount})`
      };
    }

    if (wordCount > MAX_WORDS) {
      return {
        valid: false,
        wordCount,
        error: `Answer must not exceed ${MAX_WORDS} words (currently ${wordCount})`
      };
    }

    return { valid: true, wordCount };
  };

  const getWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  const handleInputChange = (questionId, value) => {
    // Prevent editing for sample projects
    if (project?.isSample) {
      return;
    }

    setFormData({
      ...formData,
      [questionId]: value,
    });

    // Clear validation error when user types
    if (validationErrors[questionId]) {
      setValidationErrors({
        ...validationErrors,
        [questionId]: null
      });
    }
  };

  const handleBlur = (questionId, value) => {
    const validation = validateAnswer(value);
    if (!validation.valid) {
      setValidationErrors({
        ...validationErrors,
        [questionId]: validation.error
      });
    }

    // Clear Foundation error highlighting if user fills the field
    if (value && value.trim() && foundationErrors.has(questionId)) {
      const newErrors = new Set(foundationErrors);
      newErrors.delete(questionId);
      setFoundationErrors(newErrors);
    }
  };

  const checkFoundationCompletion = () => {
    if (!questions) return { complete: false, unansweredQuestions: [] };

    const foundationTabs = questions.tabs.filter(tab => tab.phase === 'foundation');
    const unansweredQuestions = [];

    foundationTabs.forEach(tab => {
      tab.questions.forEach(question => {
        const answer = formData[question.id];
        const validation = validateAnswer(answer);

        // Question must have answer AND be valid (>= 5 words)
        if (!answer || !answer.trim() || !validation.valid) {
          unansweredQuestions.push({
            questionId: question.id,
            tabId: tab.id,
            tabName: tab.name,
            questionText: question.text
          });
        }
      });
    });

    return {
      complete: unansweredQuestions.length === 0,
      unansweredQuestions,
      totalFoundationQuestions: foundationTabs.reduce((sum, tab) => sum + tab.questions.length, 0)
    };
  };

  const handleViewPlan = () => {
    // Check Foundation completion before allowing plan view
    const foundationCheck = checkFoundationCompletion();

    if (!foundationCheck.complete) {
      // Set error state
      setError(`Foundation components must be 100% complete before creating a plan. Please answer all ${foundationCheck.totalFoundationQuestions} questions in Scope, Segment, and People tabs. (${foundationCheck.unansweredQuestions.length} questions remaining)`);

      // Highlight all unanswered Foundation questions
      const errorSet = new Set(foundationCheck.unansweredQuestions.map(q => q.questionId));
      setFoundationErrors(errorSet);

      // Navigate to first unanswered question
      if (foundationCheck.unansweredQuestions.length > 0) {
        const firstUnanswered = foundationCheck.unansweredQuestions[0];
        const tabIndex = questions.tabs.findIndex(t => t.id === firstUnanswered.tabId);
        if (tabIndex !== -1) {
          setActiveTab(tabIndex);
          // Scroll to first unanswered question after tab change
          setTimeout(() => {
            const element = document.getElementById(firstUnanswered.questionId);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.focus();
            }
          }, 100);
        }
      }
      return;
    }

    // Clear any Foundation errors
    setFoundationErrors(new Set());
    setError('');

    // Proceed to plan view
    handleSave(true).then(() => {
      navigate(`/projects/${projectId}/plan`);
    });
  };

  const handleSave = async (isAutoSave = false) => {
    // Prevent saving for sample projects
    if (project?.isSample) {
      return;
    }

    setSaving(true);
    setSaveMessage('');
    setError('');

    try {
      const response = await projectsAPI.saveFormData(projectId, formData);
      setProject(response.data.data);
      setLastSaved(new Date());
      setValidationErrors({}); // Clear validation errors on successful save

      if (!isAutoSave) {
        setSaveMessage('Progress saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (err) {
      console.error('Save error:', err);

      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const errors = {};
        err.response.data.errors.forEach(e => {
          errors[e.questionId] = e.error;
        });
        setValidationErrors(errors);
        setError('Some answers do not meet minimum requirements. Please review the highlighted fields.');
      } else {
        setError(err.response?.data?.message || 'Failed to save progress');
      }
    }

    setSaving(false);
  };

  const handleTabChange = (tabIndex) => {
    // Save before switching tabs
    if (Object.keys(formData).length > 0) {
      handleSave(true);
    }
    setActiveTab(tabIndex);
  };

  const calculateTabProgress = (tab) => {
    const answeredQuestions = tab.questions.filter(
      (q) => formData[q.id] && formData[q.id].trim() !== ''
    ).length;
    return Math.round((answeredQuestions / tab.questions.length) * 100);
  };

  if (loading) {
    return (
      <div className="discovery-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!questions || !project) {
    return (
      <div className="discovery-container">
        <div className="container py-4">
          <div className="alert alert-error">
            Failed to load project data. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const currentTab = questions.tabs[activeTab];
  const isSample = project?.isSample;

  return (
    <div className="discovery-container">
      <div className="discovery-header">
        <div className="container">
          <div className="discovery-header-content">
            <div>
              <h1>{project.name}</h1>
              {isSample && (
                <span className="sample-badge" style={{ marginLeft: '10px', fontSize: '0.9em' }}>📝 Sample Project - Read Only</span>
              )}
              <p className="discovery-subtitle">{project.description}</p>
            </div>
            <div className="discovery-header-actions">
              {!isSample && (
                <span className="save-status">
                  {saving ? (
                    'Saving...'
                  ) : lastSaved ? (
                    `Last saved: ${lastSaved.toLocaleTimeString()}`
                  ) : (
                    'Not saved yet'
                  )}
                </span>
              )}
              {!isSample && (
                <button className="btn btn-outline btn-sm" onClick={() => handleSave()}>
                  Save Progress
                </button>
              )}
              {project.completionPercentage > 0 && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleViewPlan}
                >
                  View Plan
                </button>
              )}
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/projects')}
              >
                Back to Projects
              </button>
            </div>
          </div>

          <div className="overall-progress">
            <div className="progress-info">
              <span>Overall Progress</span>
              <span className="progress-percentage">
                {project.completionPercentage}%
              </span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${project.completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {error && <div className="alert alert-error">{error}</div>}
        {saveMessage && <div className="alert alert-success">{saveMessage}</div>}

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            {questions.tabs.map((tab, index) => {
              const progress = calculateTabProgress(tab);
              return (
                <button
                  key={tab.id}
                  className={`tab ${activeTab === index ? 'active' : ''}`}
                  onClick={() => handleTabChange(index)}
                >
                  <span className="tab-name">{tab.name}</span>
                  <span className="tab-progress">{progress}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Current Tab Content */}
        <div className="tab-content">
          <div className="tab-content-header">
            <h2>{currentTab.name}</h2>
            <p className="tab-description">{currentTab.description}</p>
            {componentVideos && componentVideos[currentTab.name] && (
              <div className="component-video-help">
                <a
                  href={componentVideos[currentTab.name].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="video-help-link"
                >
                  📺 Learn More - Watch Video: {componentVideos[currentTab.name].title}
                </a>
              </div>
            )}
          </div>

          <div className="questions-list">
            {currentTab.questions.map((question, index) => {
              const wordCount = getWordCount(formData[question.id]);
              const hasError = validationErrors[question.id];
              const isAnswered = formData[question.id] && formData[question.id].trim() !== '' && !hasError;
              const isFoundationError = foundationErrors.has(question.id);

              return (
                <div key={question.id} className="question-card">
                  <div className="question-header">
                    <label htmlFor={question.id} className="question-label">
                      <span className="question-number">
                        {index + 1}.
                      </span>
                      {question.text}
                    </label>
                    {isAnswered && (
                      <span className="question-answered">✓ Answered</span>
                    )}
                  </div>
                  <textarea
                    id={question.id}
                    className={`form-control ${hasError ? 'invalid' : ''} ${isAnswered ? 'valid' : ''} ${isFoundationError ? 'foundation-error' : ''}`}
                    value={formData[question.id] || ''}
                    onChange={(e) => handleInputChange(question.id, e.target.value)}
                    onBlur={(e) => handleBlur(question.id, e.target.value)}
                    placeholder={isSample ? "Sample answer (read-only)" : "Enter your answer here..."}
                    rows="4"
                    readOnly={isSample}
                    disabled={isSample}
                  />
                  <div className="question-footer">
                    <span className={`word-count ${wordCount < MIN_WORDS && wordCount > 0 ? 'warning' : ''} ${wordCount >= MIN_WORDS ? 'success' : ''}`}>
                      {wordCount} {wordCount === 1 ? 'word' : 'words'}
                      {wordCount > 0 && wordCount < MIN_WORDS && ` (minimum ${MIN_WORDS})`}
                      {wordCount > MAX_WORDS && ` (maximum ${MAX_WORDS})`}
                    </span>
                    {hasError && (
                      <span className="validation-error">{hasError}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="tab-navigation">
            <button
              className="btn btn-outline"
              onClick={() => handleTabChange(Math.max(0, activeTab - 1))}
              disabled={activeTab === 0}
            >
              ← Previous Section
            </button>

            <button className="btn btn-primary" onClick={() => handleSave()}>
              Save Progress
            </button>

            {activeTab < questions.tabs.length - 1 ? (
              <button
                className="btn btn-outline"
                onClick={() =>
                  handleTabChange(Math.min(questions.tabs.length - 1, activeTab + 1))
                }
              >
                Next Section →
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={handleViewPlan}>
                View Migration Plan →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Discovery;
